import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import { beginCardWrite, recordCardSnapshot } from '../lib/cardSnapshots'
import type {
  Card, CardCreate, CardListResponse, CardLocateResponse, CardSearchResponse, CardUpdate,
} from '../types'
import type { CardPlacement } from '../utils/cardOrder'
import type { CardSelectionRequest, DeckCardCounts } from '../utils/deckSelection'

/** What `PATCH .../move` answers: where the card ended up, in the deck's terms. */
export interface CardMoveResult {
  card_id: string
  /** Zero-based index in the full deck — not in the caller's loaded window. */
  index: number
  position: number
  total: number
}

export interface BatchCreateItem {
  term: string
  definition: string
  client_key: string
}

export interface BatchCreateResponse {
  created: number
  skipped: number
  cards: import('../types').Card[]
}

/**
 * The deck feed's pages.
 *
 * `anchorPage` is where the feed STARTS. It is 1 for every ordinary visit, and
 * only a deliberate navigation — quick jump by number, or a `?card=<uuid>` deep
 * link — ever moves it. That is the whole of brief §21.3 item 1: the server can
 * now say which page a card is on, so reaching card 800 is one request for page
 * 27 instead of twenty-seven requests for pages 1..27.
 *
 * It is part of the query key on purpose. A new anchor is a different window
 * onto the deck, not more of the same one, and letting TanStack start a fresh
 * infinite query is exactly right: the old pages are dropped rather than
 * concatenated to pages that do not adjoin them. The key still begins
 * `['cards', deckId]`, so every existing mutation's invalidation keeps matching.
 *
 * There is deliberately no `getPreviousPageParam`. Fetching backwards prepends
 * content ABOVE the reader, which needs a scroll correction to keep the page
 * still — and a scroll write of that kind on this surface is the sort of thing
 * ADR-067 is careful about. An anchored feed instead offers an explicit "back to
 * the start" exit, which costs one request and contends with nothing.
 *
 * `ready` is how a caller says "the anchor is not decided yet". A deep link
 * cannot know its page until `locate` answers, and page 1 is not a harmless
 * guess in the meantime: it is a request for a page nobody asked for, and it
 * renders a window the reader did not ask to see. Holding here means one deep
 * link costs exactly one page request, for the page the card is actually on.
 */
export function useCardsInfinite(deckId: string, pageSize = 30, anchorPage = 1, ready = true) {
  return useInfiniteQuery({
    queryKey: ['cards', deckId, 'infinite', pageSize, anchorPage],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const { data } = await api.get<CardListResponse>(
        `/api/v1/decks/${deckId}/cards?page=${pageParam}&page_size=${pageSize}`,
      )
      return data
    },
    initialPageParam: anchorPage,
    getNextPageParam: (lastPage: CardListResponse) => {
      const loaded = lastPage.page * lastPage.page_size
      return loaded < lastPage.total ? lastPage.page + 1 : undefined
    },
    enabled: !!deckId && ready,
  })
}

/**
 * Deck-wide card search (brief §15).
 *
 * Server-side, and with no client-side fallback anywhere: if this fails the
 * caller shows a failure. Quietly filtering the loaded pages instead would
 * reproduce the exact defect being removed — a confident "not found" for a word
 * that is in the deck, indistinguishable to the reader from a true miss.
 *
 * Paginated like the feed, so a query matching hundreds of cards streams rather
 * than arriving at once; `total` on every page is the deck-wide match count.
 */
export function useCardSearchInfinite(deckId: string, query: string, pageSize = 30) {
  const q = query.trim()
  return useInfiniteQuery({
    queryKey: ['cards', deckId, 'search', q, pageSize],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const { data } = await api.get<CardSearchResponse>(
        `/api/v1/decks/${deckId}/cards/search`,
        { params: { q, page: pageParam, page_size: pageSize } },
      )
      return data
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: CardSearchResponse) => {
      const loaded = lastPage.page * lastPage.page_size
      return loaded < lastPage.total ? lastPage.page + 1 : undefined
    },
    enabled: !!deckId && q.length > 0,
    // A query is retyped a character at a time; keeping the previous result
    // visible stops the list flashing empty between keystrokes.
    placeholderData: (prev) => prev,
  })
}

/** Where a card sits in its deck, in one request — the deep link's replacement
 * for walking pages until the card appears (brief §21.3 item 1). */
export function useLocateCard(deckId: string | undefined, cardId: string | null) {
  return useQuery({
    queryKey: ['cards', deckId, 'locate', cardId],
    queryFn: async () => {
      const { data } = await api.get<CardLocateResponse>(
        `/api/v1/decks/${deckId}/cards/locate`,
        { params: { card_id: cardId } },
      )
      return data
    },
    enabled: !!deckId && !!cardId,
    // A card's ordinal only moves when the deck is reordered, and every reorder
    // invalidates `['cards', deckId]` already.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useCreateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ deckId, body }: { deckId: string; body: CardCreate }) => {
      const { data } = await api.post<Card>(`/api/v1/decks/${deckId}/cards`, body)
      // The response is the authoritative card and used to be discarded in
      // favour of asking the list for the same thing a round trip later. The
      // editing surface reads it back from here, so the mount that follows a
      // create already knows what was created (see lib/cardSnapshots).
      recordCardSnapshot(data)
      return data
    },
    onSuccess: (card) => {
      qc.invalidateQueries({ queryKey: ['cards', card.deck_id] })
      qc.invalidateQueries({ queryKey: ['deck', card.deck_id] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

export function useUpdateCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CardUpdate }) => {
      // Registered as in flight for the CARD, not for this component: the
      // commonest way to finish editing unmounts the surface that issued the
      // write, and the next mount of the same card is precisely the one that
      // must not build a full-replace payload from a picture this write is
      // about to supersede (docs/diagnostics/deck-save-visibility-window-
      // 2026-08-11.md, and lib/cardSnapshots for the mechanism).
      const write = api.patch<Card>(`/api/v1/cards/${id}`, body).then(({ data }) => {
        recordCardSnapshot(data)
        return data
      })
      return beginCardWrite(id, write)
    },
    onSuccess: (card) => {
      qc.invalidateQueries({ queryKey: ['cards', card.deck_id] })
      qc.invalidateQueries({ queryKey: ['deck', card.deck_id] })
      qc.invalidateQueries({ queryKey: ['decks'] })
      // An edit can change the Term, and the Term is what `/cards/{id}/senses`
      // answers about — candidates, and whether the card's link is still among
      // them. This query has a 60s staleTime, so without this a reader pages
      // through the PREVIOUS word's senses for up to a minute after the edit,
      // and paging PATCHes one of them: an id sourced from before the edit,
      // which the endpoint cannot tell from a current one (ADR-070 §6).
      qc.invalidateQueries({ queryKey: ['card-senses', card.id] })
    },
  })
}

export function useDeleteCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, deckId }: { id: string; deckId: string }) => {
      await api.delete(`/api/v1/cards/${id}`)
      return { id, deckId }
    },
    onSuccess: ({ deckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', deckId] })
      qc.invalidateQueries({ queryKey: ['deck', deckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

/**
 * Deck-wide card counts per filter chip (`GET /decks/{id}/cards/counts`).
 *
 * The only honest source for a number printed beside a bulk action. The chip
 * counts the feed computes are over the pages it has fetched, which is why the
 * same deck used to show `30` on one visit and `90` on the next — a difference
 * caused by cache state the reader can neither see nor control (brief §23
 * item 9). These are properties of the deck.
 *
 * Keyed under `['cards', deckId, ...]` so every existing card mutation's
 * invalidation already refreshes them; nothing needs to remember to.
 */
export function useCardCounts(deckId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['cards', deckId, 'counts'],
    queryFn: async () => {
      const { data } = await api.get<DeckCardCounts & { deck_id: string }>(
        `/api/v1/decks/${deckId}/cards/counts`,
      )
      return data
    },
    enabled: !!deckId && enabled,
  })
}

export interface SelectionResolveResult {
  total: number
  card_ids: string[] | null
}

/**
 * Resolve a selection to its membership, server-side.
 *
 * Only for callers that must iterate the selection themselves — Fill, which
 * applies through the per-card endpoint. Delete and Move send the selection
 * itself and never hold an id list, so there is no window in which the client
 * operates on a stale membership.
 */
export function useResolveSelection() {
  return useMutation({
    mutationFn: async ({
      deckId, selection, includeIds = false,
    }: { deckId: string; selection: CardSelectionRequest; includeIds?: boolean }) => {
      const { data } = await api.post<SelectionResolveResult>(
        `/api/v1/decks/${deckId}/cards/selection`,
        { selection },
        { params: { include_ids: includeIds } },
      )
      return data
    },
  })
}

export interface BatchDeleteResult {
  requested: number
  deleted: number
  card_ids: string[]
  /**
   * The handle Undo restores through (brief §12), or `null` when nothing was
   * deleted and there is therefore nothing to offer.
   *
   * It is a token rather than the card ids because the ids alone would not be
   * enough: the cards are gone, and with them their sides, their positions and
   * their FSRS state. What the token names is the server's snapshot of all of
   * that, taken before the delete ran. The client could not have produced it —
   * a deck-scoped delete removes cards this session never loaded.
   */
  restore_token: string | null
  restore_expires_at: string | null
}

/**
 * What a restore actually put back — never a bare success.
 *
 * The breakdown exists for one failure this cannot be allowed to hide: cards
 * returning without their scheduling. `complete` and `learning_state_complete`
 * are what the interface reads, so it can say "restored" only when everything
 * is back and say what is missing when it is not, rather than resetting a
 * reader's progress behind a cheerful toast.
 */
export interface RestoreResult {
  deck_id: string
  cards_expected: number
  cards_restored: number
  cards_already_present: number
  sides_restored: number
  learning_states_expected: number
  learning_states_restored: number
  review_logs_expected: number
  review_logs_restored: number
  milestones_expected: number
  milestones_restored: number
  word_links_dropped: number
  share_tokens_reissued: number
  complete: boolean
  learning_state_complete: boolean
  card_ids: string[]
}

/** Undo a bulk delete (`POST /decks/{id}/cards/restore`). */
export function useRestoreCards() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      deckId, restoreToken,
    }: { deckId: string; restoreToken: string }) => {
      const { data } = await api.post<RestoreResult>(
        `/api/v1/decks/${deckId}/cards/restore`, { restore_token: restoreToken },
      )
      return { ...data, deckId }
    },
    onSuccess: ({ deckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', deckId] })
      qc.invalidateQueries({ queryKey: ['deck', deckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

/**
 * Delete a selection's cards (`POST /decks/{id}/cards/delete`).
 *
 * One request over a server-resolved selection, replacing N parallel
 * `DELETE /cards/{id}` calls. Two things that change: a deck-scoped selection
 * deletes the deck's cards rather than the loaded page's, and the response
 * separates `requested` from `deleted` so a caller reports what happened
 * instead of inferring success from a 200.
 */
export function useDeleteCards() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      deckId, selection,
    }: { deckId: string; selection: CardSelectionRequest }) => {
      const { data } = await api.post<BatchDeleteResult>(
        `/api/v1/decks/${deckId}/cards/delete`, { selection },
      )
      return { ...data, deckId }
    },
    onSuccess: ({ deckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', deckId] })
      qc.invalidateQueries({ queryKey: ['deck', deckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

/**
 * Move one card next to a sibling — the single reorder primitive (ADR-067
 * Decision 6, `PATCH /decks/{id}/cards/{card_id}/move`).
 *
 * This replaces `useReorderCards`, which posted a client-computed list of
 * absolute `{id, position}` pairs to the route removed in commit 4c1c7f5. Its
 * three surviving callers — insert-at-index on create, the up/down buttons and
 * the desktop drag-and-drop — 404'd on every use; they now build a placement
 * with `relativePlacement` (`utils/cardOrder.ts`) and call this. The hook is
 * gone rather than deprecated: a registered absolute-position path is one
 * caller away from the defect ADR-067 names on its do-not-reintroduce list.
 *
 * The server recomputes the whole deck's positions from what IT reads, so a
 * caller holding one page of a large deck can still move a card correctly. Two
 * of these in flight against the same deck would read overlapping snapshots —
 * callers that issue several must do so sequentially, as `useRestoreCardOrder`
 * does and for the same reason.
 */
export function useMoveCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      deckId,
      cardId,
      placement,
    }: { deckId: string; cardId: string; placement: CardPlacement }) => {
      const { data } = await api.patch<CardMoveResult>(
        `/api/v1/decks/${deckId}/cards/${cardId}/move`,
        placement,
      )
      return { ...data, deckId }
    },
    // Returned (not fire-and-forget) so TanStack Query awaits the refetch
    // before running the mutate()-level onSuccess, which is where the caller
    // drops its optimistic `localCards` and falls back to query data. Without
    // the await, that fallback could render one frame of the still-stale
    // cached order before the invalidated refetch resolves — the card would
    // flicker back to its old position before settling into the new one.
    onSuccess: ({ deckId }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['cards', deckId] }),
        qc.invalidateQueries({ queryKey: ['deck', deckId] }),
        qc.invalidateQueries({ queryKey: ['decks'] }),
      ]),
  })
}

export interface RestoreOrderItem {
  id: string
  /** The id of the card that sat immediately before this one in the deck at
   * capture time, or `null` if this card was first. */
  afterId: string | null
}

/**
 * Puts cards back where they sat before "Move cards to another deck" — the
 * Undo path's rollback of the SOURCE deck's order, once the cards themselves
 * are back in it. Expressed the same way any other reorder is now expressed
 * (ADR-067 Decision 6): relative to a captured neighbour, never as an absolute
 * position this client is in no position to reassert — the deck may have
 * changed shape in the meantime, and an absolute integer claims something
 * about every OTHER card in the deck that a relative "put it after X" does
 * not.
 *
 * Applied one request at a time, in the given order — not `Promise.all`, and
 * this is load-bearing rather than a style choice:
 *
 *  - A card whose captured neighbour was ALSO moved (a contiguous multi-card
 *    move) can only anchor on that neighbour once the neighbour itself is back
 *    in place. Callers are expected to pass `items` sorted by the cards'
 *    original position, ascending, which combined with sequential application
 *    guarantees every anchor already exists — and is already correctly placed
 *    — by the time it is needed.
 *  - `PATCH .../move` recomputes the WHOLE deck's order from what it reads at
 *    the start of the request. Concurrent calls against the same deck would
 *    read overlapping snapshots and could clobber each other's writes.
 *
 * A failure partway through rejects the returned promise rather than being
 * swallowed. Some prefix of `items` may already have been applied at that
 * point — this function does not paper over that with a success report, and
 * neither should its caller: a rollback that only partly ran is not "cards
 * returned, order may have shifted", it is a failure, and the interface must
 * say so plainly rather than downgrade it to a hedge.
 */
export function useRestoreCardOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ deckId, items }: { deckId: string; items: RestoreOrderItem[] }) => {
      for (const { id, afterId } of items) {
        await api.patch(
          `/api/v1/decks/${deckId}/cards/${id}/move`,
          afterId ? { placement: 'after', anchor_card_id: afterId } : { placement: 'start' },
        )
      }
      return { deckId }
    },
    onSuccess: ({ deckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', deckId] })
      qc.invalidateQueries({ queryKey: ['deck', deckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

export interface BatchMoveResult {
  moved: number
  skipped: number
  card_ids: string[]
  /**
   * Where each moved card sat in the SOURCE deck, captured server-side before
   * the move. Undo replays these as relative moves.
   *
   * It comes from the server because the client cannot compute it: for a
   * deck-scoped move most of these cards were never loaded here, so a
   * client-derived order would restore only the fragment this session happened
   * to hold and present that as a completed undo.
   */
  original_order: { id: string; after_id: string | null }[]
}

export interface BatchCopyResult {
  copied: number
  /** Cards this same operation had already created — i.e. it is being retried.
   * A second deliberate copy is not a skip: it makes new cards. */
  skipped: number
  card_ids: string[]
  /** Names this operation's own cards for Undo. The ids above must not be used
   * for that: they said nothing about which operation created them, so an Undo
   * acting on them deleted an earlier copy's cards. */
  undo_token: string | null
  undo_expires_at: string | null
}

export interface CopyUndoResult {
  deck_id: string
  cards_expected: number
  cards_removed: number
  card_ids: string[]
  complete: boolean
}

export function useBatchCreateCards() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ deckId, items }: { deckId: string; items: BatchCreateItem[] }) => {
      const { data } = await api.post<BatchCreateResponse>(
        `/api/v1/decks/${deckId}/cards/batch`,
        { items },
      )
      return { ...data, deckId }
    },
    onSuccess: ({ deckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', deckId] })
      qc.invalidateQueries({ queryKey: ['deck', deckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

/**
 * Move a selection's cards into another deck.
 *
 * Takes the selection rather than a list of ids, so "move the whole deck"
 * reaches the server as a scope. Under the old contract this posted whatever
 * the client had paged in: Move on a 93-card deck with 30 loaded relocated 30
 * cards and reported the reader's "all" as done (brief §23 item 9).
 */
export function useMoveCards() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      sourceDeckId,
      targetDeckId,
      selection,
    }: {
      sourceDeckId: string
      targetDeckId: string
      selection: CardSelectionRequest
    }) => {
      const { data } = await api.post<BatchMoveResult>(
        `/api/v1/decks/${sourceDeckId}/cards/move`,
        { selection, target_deck_id: targetDeckId },
      )
      return { ...data, sourceDeckId, targetDeckId }
    },
    onSuccess: ({ sourceDeckId, targetDeckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', sourceDeckId] })
      qc.invalidateQueries({ queryKey: ['deck', sourceDeckId] })
      qc.invalidateQueries({ queryKey: ['cards', targetDeckId] })
      qc.invalidateQueries({ queryKey: ['deck', targetDeckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

/** Copy a selection's cards into another deck — same selection contract as
 * `useMoveCards`, for the same reason.
 *
 * `operationId` identifies *this* copy. The caller generates one per confirmed
 * copy and reuses it only when retrying that same copy after a failure, which
 * is the whole distinction the server needs: same id means "you may already
 * have done this", a new id means "do it again". Copying the same cards twice
 * used to produce one card because the server keyed copies on
 * (source, target) alone and could not tell those two cases apart. */
export function useCopyCards() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      sourceDeckId,
      targetDeckId,
      selection,
      operationId,
    }: {
      sourceDeckId: string
      targetDeckId: string
      selection: CardSelectionRequest
      operationId: string
    }) => {
      const { data } = await api.post<BatchCopyResult>(
        `/api/v1/decks/${sourceDeckId}/cards/copy`,
        { selection, target_deck_id: targetDeckId, operation_id: operationId },
      )
      return { ...data, sourceDeckId, targetDeckId }
    },
    onSuccess: ({ sourceDeckId, targetDeckId }) => {
      qc.invalidateQueries({ queryKey: ['cards', sourceDeckId] })
      qc.invalidateQueries({ queryKey: ['deck', sourceDeckId] })
      qc.invalidateQueries({ queryKey: ['cards', targetDeckId] })
      qc.invalidateQueries({ queryKey: ['deck', targetDeckId] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}

/**
 * Undo one copy — remove the cards that copy created, and only those.
 *
 * Goes through the operation's own token rather than deleting the ids the copy
 * returned. Those ids were derived from (source card, target deck) and so were
 * shared by every copy of those cards into that deck: undoing one copy deleted
 * whichever copy was there, including one an earlier, unrelated operation had
 * made. The token names a single operation and is spent on first use.
 *
 * `deckId` is the deck the cards were copied INTO.
 */
export function useUndoCardCopy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ deckId, undoToken }: { deckId: string; undoToken: string }) => {
      const { data } = await api.post<CopyUndoResult>(
        `/api/v1/decks/${deckId}/cards/copy-undo`,
        { undo_token: undoToken },
      )
      return data
    },
    onSuccess: ({ deck_id }) => {
      qc.invalidateQueries({ queryKey: ['cards', deck_id] })
      qc.invalidateQueries({ queryKey: ['deck', deck_id] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}
